import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Home() {
  const [isVisible, setIsVisible] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    document.title = 'Negotrix - Commercial Intelligence & Negotiation Partner for Growing Companies';
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  return (
    <div className={`min-h-screen bg-cipher-black text-white font-sans selection:bg-cipher-hypergreen selection:text-cipher-black transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Header />
      
      {/* 1. HERO */}
      <section className="relative min-h-screen flex items-center pt-20 pb-12 px-6 lg:px-12 overflow-hidden">
         <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cipher-hypergreen/5 rounded-full blur-[100px] pointer-events-none transform translate-x-1/3 -translate-y-1/3 animate-float"></div>
         
         <div className="max-w-[1400px] mx-auto w-full relative z-10">
           <h1 className="text-[8vw] leading-[0.95] font-black tracking-tighter mb-6 animate-slide-in-up">
             <span className="block text-white">Every vendor wants</span>
             <span className="block text-white">the best deal.</span>
             <span className="block text-cipher-hypergreen">So should you.</span>
           </h1>
           
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 mt-10 animate-slide-in-up" style={{animationDelay: '200ms'}}>
             <div>
               <p className="text-lg md:text-xl lg:text-2xl font-medium leading-tight text-gray-300 mb-6">
                  Negotrix is the commercial intelligence and negotiation partner for growing companies, helping you negotiate smarter, benchmark pricing, and build commercial confidence without adding another internal team.
               </p>
               <p className="text-base text-gray-500 mb-8">
                 That's where Negotrix comes in.
                 <span className="text-white ml-2">Your external commercial negotiation team.</span>
               </p>
               <div className="space-x-3">
                 <button 
                   onClick={() => setShowContactModal(true)}
                   className="inline-flex items-center justify-center px-6 py-3 text-xs tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300 animate-glow-pulse"
                 >
                   Book a commercial review
                 </button>
                 <a href="/how-it-works" className="inline-flex items-center justify-center px-6 py-3 text-xs tracking-wide font-bold uppercase rounded-full text-white border border-gray-700 hover:border-cipher-hypergreen hover:text-cipher-hypergreen transition-all duration-300">
                   How it Works
                 </a>
               </div>
             </div>
           </div>
         </div>
      </section>

      {/* 2. CREDIBILITY / POSITIONING */}
      <section className="py-12 bg-cipher-dark border-t border-gray-900">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
           <div className="mb-8">
             <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-4">Built different</h2>
             <h3 className="text-3xl lg:text-4xl font-black tracking-tight max-w-4xl">Trust signals</h3>
           </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 border-b border-gray-800 pb-8">
              {[
                "Built by operators who have managed large-scale enterprise spend.",
                "Experience across SaaS, cloud, infrastructure, marketing and enterprise procurement.",
                "Success-fee model aligned with customer outcomes.",
                "Independent and vendor-neutral.",
                "We negotiate in your interest, not the vendor's.",
                "Deep commercial expertise in vendor negotiation and procurement."
              ].map((item, idx) => (
                <div key={idx} className={`flex items-start space-x-3 p-3 rounded-xl hover:bg-cipher-black/50 transition-colors duration-300 animate-fade-in-up`} style={{animationDelay: `${idx * 100}ms`}}>
                  <span className="text-cipher-hypergreen text-xl font-bold leading-none flex-shrink-0">✓</span>
                  <span className="text-base font-bold text-white leading-tight">{item}</span>
                </div>
              ))}
            </div>

           <div className="relative rounded-[2rem] overflow-hidden border border-gray-800">
             <div className="p-8 md:p-10 text-center">
               <h4 className="text-xl md:text-3xl font-black text-white mb-4 leading-tight">You have deep commercial expertise in your business.</h4>
               <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">We bring deep commercial expertise in vendor negotiation and procurement. Together, we make better commercial decisions.</p>
             </div>
           </div>
           
         </div>
      </section>

      {/* 3. WHY VENDOR SPEND GETS EXPENSIVE */}
      <section className="py-16 bg-cipher-black relative">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
           <div className="mb-10">
             <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-4">The Problem</h2>
             <h3 className="text-4xl lg:text-5xl font-black tracking-tight max-w-4xl">
               Why vendor spend quietly gets expensive
             </h3>
           </div>

           <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
             <div>
               <p className="text-lg md:text-xl font-medium leading-tight text-gray-300 mb-6">
                 Growth moves faster than procurement.
               </p>
               <ul className="space-y-3 text-base text-gray-300">
                 <li className="flex items-start"><span className="text-cipher-hypergreen mr-3">→</span> Teams buy tools independently.</li>
                 <li className="flex items-start"><span className="text-cipher-hypergreen mr-3">→</span> Contracts auto-renew.</li>
                 <li className="flex items-start"><span className="text-cipher-hypergreen mr-3">→</span> Discounts disappear.</li>
                 <li className="flex items-start"><span className="text-cipher-hypergreen mr-3">→</span> Pricing lacks transparency.</li>
                 <li className="flex items-start"><span className="text-cipher-hypergreen mr-3">→</span> Negotiations happen without market benchmarks.</li>
               </ul>
             </div>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
               {[
                 "SaaS",
                 "Cloud",
                 "AI Tools",
                 "Marketing",
                 "Enterprise Software",
                 "Vendors"
               ].map((category, idx) => (
                 <div key={idx} className="flex items-center p-4 rounded-xl bg-cipher-dark border border-gray-800">
                   <span className="text-cipher-hypergreen text-xl mr-3 font-bold">✓</span>
                   <span className="text-lg font-bold text-white">{category}</span>
                 </div>
               ))}
             </div>
           </div>
         </div>
      </section>

      {/* 4. HOW NEGOTRIX WORKS */}
      <section className="py-16 bg-cipher-dark">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
           <div className="mb-10">
             <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-4">Process</h2>
               <h3 className="text-4xl lg:text-5xl font-black tracking-tight max-w-4xl">Commercial confidence.</h3>
           </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { num: '01', title: 'Understand', desc: 'We analyze your commercial commitments, contracts and spending patterns.' },
                { num: '02', title: 'Benchmark', desc: 'We compare pricing, contract structures and commercial terms against the market.' },
                { num: '03', title: 'Negotiate', desc: 'Our commercial experts negotiate directly with vendors on your behalf.' },
                { num: '04', title: 'Deliver', desc: 'You retain the value. We only succeed when you do.' }
              ].map((step, idx) => (
                <div key={idx} className={`bg-cipher-black p-8 rounded-[2rem] border border-gray-800 animate-fade-in-up hover:border-cipher-hypergreen transition-all duration-300`} style={{animationDelay: `${idx * 150}ms`}}>
                  <div className="text-cipher-hypergreen text-sm tracking-widest uppercase font-bold mb-3">0{step.num}</div>
                  <h4 className="text-2xl font-bold text-white mb-3">{step.title}</h4>
                   <p className="text-gray-300 text-lg leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
         </div>
      </section>

      {/* 5. WE HELP ACROSS YOUR ENTIRE VENDOR ECOSYSTEM */}
      <section className="py-16 bg-cipher-black">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
           <div className="mb-10">
             <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-4">What we do</h2>
               <h3 className="text-4xl lg:text-5xl font-black tracking-tight max-w-4xl">Your external team for vendor intelligence.</h3>
           </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "SaaS & Enterprise Software", desc: "Renewals, licensing, consolidation, pricing negotiations." },
                { title: "Cloud & Infrastructure", desc: "Commercial strategy for cloud commitments and infrastructure contracts." },
                { title: "Marketing Platforms", desc: "Agency agreements, ad-tech, attribution, analytics and marketing vendors." },
                { title: "AI & Developer Tools", desc: "Manage rapidly growing AI and engineering software spend." },
                { title: "Professional Services", desc: "Consultants, outsourcing partners and recurring service contracts." },
                { title: "Strategic Vendors", desc: "Long-term supplier agreements across business functions." }
              ].map((item, idx) => (
                <div key={idx} className={`group p-6 rounded-[2rem] border border-gray-800 hover:border-cipher-hypergreen hover:-translate-y-1 transition-all duration-300 animate-fade-in-up animate-border-glow`} style={{animationDelay: `${idx * 100}ms`}}>
                  <h4 className="text-lg font-bold text-white mb-3">{item.title}</h4>
                  <p className="text-gray-300 text-base leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
         </div>
      </section>

      {/* 6. WHY FOUNDERS CHOOSE NEGOTRIX */}
      <section className="py-16 bg-cipher-dark">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
           <div className="mb-10">
             <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-4">Why founders choose Negotrix</h2>
             <h3 className="text-4xl lg:text-5xl font-black tracking-tight max-w-4xl">
               Not because "We save money."
             </h3>
           </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                "We have negotiated from the buyer's side.",
                "We understand enterprise pricing models.",
                "We know how vendors structure commercial offers.",
                "We work alongside your finance and procurement teams.",
                "Success-based pricing."
              ].map((item, idx) => (
                <div key={idx} className={`flex items-start p-6 rounded-[2rem] border border-gray-800 hover:border-cipher-hypergreen hover:-translate-y-1 transition-all duration-300 animate-fade-in-up`} style={{animationDelay: `${idx * 100}ms`}}>
                  <span className="text-cipher-hypergreen text-2xl mr-3 font-bold leading-none">✓</span>
                  <span className="text-lg font-bold text-white leading-tight">{item}</span>
                </div>
              ))}
            </div>
         </div>
      </section>

      {/* 7. THE LEVERAGE GAP */}
      <section className="py-16 bg-cipher-black">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
           <div className="mb-10">
             <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-4">The biggest missing story</h2>
             <h3 className="text-4xl lg:text-5xl font-black tracking-tight max-w-4xl">
               Most companies negotiate only once.
               <br />
               <span className="text-cipher-hypergreen">Vendors negotiate every day.</span>
             </h3>
           </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
             <div>
               <p className="text-lg md:text-xl font-medium leading-tight text-gray-300 mb-6 flex items-start">
                 <span className="text-cipher-hypergreen text-xl mr-3 font-bold leading-none">✓</span>
                 Your vendors negotiate thousands of contracts every year.
               </p>
               <p className="text-lg md:text-xl font-medium leading-tight text-gray-300 mb-6 flex items-start">
                 <span className="text-cipher-hypergreen text-xl mr-3 font-bold leading-none">✓</span>
                 Most growing companies negotiate only a handful.
               </p>
             </div>
             <div className="flex items-center">
               <div>
                 <p className="text-xl md:text-2xl font-medium leading-tight text-gray-300 mb-6 flex items-start">
                   <span className="text-cipher-hypergreen text-2xl mr-3 font-bold leading-none">✓</span>
                   <span>Experience creates leverage.</span>
                 </p>
                 <p className="text-xl md:text-2xl font-medium leading-tight text-gray-300 flex items-start">
                   <span className="text-cipher-hypergreen text-2xl mr-3 font-bold leading-none">✓</span>
                   <span>Negotrix helps level the playing field.</span>
                 </p>
               </div>
             </div>
           </div>
         </div>
      </section>

      {/* 8. TYPICAL ENGAGEMENTS */}
      <section className="py-16 bg-cipher-dark">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
           <div className="mb-10">
             <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-4">Typical engagements</h2>
             <h3 className="text-4xl lg:text-5xl font-black tracking-tight max-w-4xl">Sound familiar?</h3>
           </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "We're renewing Slack.",
                "Our cloud bill has doubled.",
                "We're evaluating HubSpot.",
                "We're buying Salesforce.",
                "We're locked into a 3-year contract.",
                "Our AI spend is exploding."
              ].map((item, idx) => (
                <div key={idx} className={`flex items-start p-6 rounded-[2rem] border border-gray-800 bg-cipher-black hover:border-cipher-hypergreen hover:-translate-y-1 transition-all duration-300 animate-fade-in-up`} style={{animationDelay: `${idx * 80}ms`}}>
                  <span className="text-cipher-hypergreen text-xl mr-3 font-bold leading-none">✓</span>
                  <span className="text-lg font-bold text-white leading-tight">{item}</span>
                </div>
              ))}
            </div>
         </div>
      </section>

      {/* 9. CALL TO ACTION */}
      <section className="py-16 lg:py-24 bg-cipher-black border-t border-gray-800">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
           <h2 className="text-[6vw] leading-[0.95] font-black tracking-tighter text-white mb-8 hover:text-cipher-hypergreen transition-colors cursor-pointer animate-bounce-in" onClick={() => setShowContactModal(true)}>
              Commercial confidence.<br />
              No upfront commitment.<br />
              <span className="text-cipher-hypergreen">No risk.</span>
           </h2>
           <button 
             onClick={() => setShowContactModal(true)}
             className="inline-flex items-center justify-center px-8 py-4 text-sm tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300 animate-glow-pulse"
           >
             Book a commercial review
           </button>
         </div>
       </section>

      <Footer />

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-0">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowContactModal(false)}></div>
          
          <div className="relative bg-cipher-black border border-gray-800 w-full max-w-2xl rounded-[2rem] p-8 sm:p-16 shadow-2xl animate-slide-in-up">
            <button 
              onClick={() => setShowContactModal(false)}
              className="absolute top-8 right-8 w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-cipher-hypergreen hover:text-black transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            
            <h3 className="text-4xl lg:text-5xl font-black text-white mb-4">Let's talk.</h3>
            <p className="text-gray-400 text-lg mb-10 font-medium">Choose a time or shoot us an email. No upfront fees. No risk.</p>
            
            <div className="space-y-4">
              <a href="tel:9540099199" className="flex items-center p-6 rounded-2xl bg-gray-900 hover:bg-cipher-hypergreen transition-colors group">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mr-6 group-hover:bg-black/20 transition-colors">
                  <i className="ri-phone-fill text-2xl text-white group-hover:text-black"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2 group-hover:text-black/60">Call Us Directly</div>
                  <div className="text-2xl font-bold text-white group-hover:text-black">+91 9540099199</div>
                </div>
              </a>
              
              <a href="mailto:amit@negotrix.com" className="flex items-center p-6 rounded-2xl bg-gray-900 hover:bg-cipher-hypergreen transition-colors group">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mr-6 group-hover:bg-black/20 transition-colors">
                  <i className="ri-mail-fill text-2xl text-white group-hover:text-black"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2 group-hover:text-black/60">Email Our Team</div>
                  <div className="text-2xl font-bold text-white group-hover:text-black">amit@negotrix.com</div>
                </div>
              </a>
              
              <a href="https://calendly.com/amit-negotrix/30min" target="_blank" rel="noopener noreferrer" className="flex items-center p-6 rounded-2xl border-2 border-gray-800 hover:border-cipher-hypergreen transition-colors group mt-8">
                <div className="w-16 h-16 bg-cipher-hypergreen rounded-full flex items-center justify-center mr-6 group-hover:bg-cipher-hypergreen transition-colors">
                  <i className="ri-calendar-check-fill text-2xl text-black"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Find a Time</div>
                  <div className="text-2xl font-bold text-white">Book on Calendly</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
